/**
 * BundleTagsManager — chip-style tag picker, grouped by category.
 *
 * Modes:
 *  - "selector"      : interactive — user can pick & unpick tags. Calls onChange(tags).
 *  - "display"       : read-only — shows tags as compact chips.
 *
 * Props:
 *  - value      : string[]  currently-selected tag names (no '#')
 *  - onChange   : (next: string[]) => void   only for selector mode
 *  - mode       : "selector" | "display"
 *  - maxTags    : number  cap selectable tags (default 30)
 *  - minTags    : number  show count helper "Pick at least N"
 *  - compact    : boolean  smaller chips, used inside cards
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Plus, X, Check, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cachedCatalog = null;

export default function BundleTagsManager({
  value = [],
  onChange,
  mode = 'selector',
  maxTags = 30,
  minTags = 0,
  compact = false,
}) {
  const [catalog, setCatalog] = useState(_cachedCatalog);
  const [query, setQuery] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    if (_cachedCatalog) return;
    fetch(`${API}/tags/catalog`)
      .then((r) => r.json())
      .then((d) => {
        _cachedCatalog = d.catalog;
        setCatalog(d.catalog);
      })
      .catch(() => setCatalog({}));
  }, []);

  const selected = useMemo(() => new Set(value.map((t) => t.replace(/^#/, ''))), [value]);

  const toggle = (tag) => {
    if (mode !== 'selector' || !onChange) return;
    if (selected.has(tag)) {
      onChange(value.filter((t) => t.replace(/^#/, '') !== tag));
    } else {
      if (value.length >= maxTags) {
        toast.error(`You can pick up to ${maxTags} tags`);
        return;
      }
      onChange([...value, tag]);
    }
  };

  const filteredCatalog = useMemo(() => {
    if (!catalog) return {};
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    const out = {};
    for (const [cat, tags] of Object.entries(catalog)) {
      const filtered = tags.filter((t) => t.toLowerCase().includes(q));
      if (filtered.length) out[cat] = filtered;
    }
    return out;
  }, [catalog, query]);

  // ── Display mode — read-only chip strip ──────────────────────────────
  if (mode === 'display') {
    if (!value.length) return null;
    return (
      <div className={`flex flex-wrap gap-1.5 ${compact ? '' : 'mt-1'}`} data-testid="bundle-tags-display">
        {value.map((t) => {
          const clean = t.replace(/^#/, '');
          return (
            <span
              key={clean}
              className={`inline-flex items-center rounded-full bg-bundle-green/10 text-bundle-green border border-bundle-green/20 ${compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'} font-bold tracking-wide`}
            >
              #{clean}
            </span>
          );
        })}
      </div>
    );
  }

  // ── Selector mode ────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="bundle-tags-selector">
      {/* Header with counter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-bold text-bundle-green">Your Bundle Tags</p>
          <p className="text-[11px] text-gray-500">
            {value.length}/{maxTags} selected
            {minTags > 0 && value.length < minTags && (
              <span className="text-bundle-gold-dark font-bold ml-1">• Pick at least {minTags}</span>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="bundle-tags-suggest-btn"
          onClick={() => setSuggesting(true)}
          className="text-[11px] font-bold text-bundle-green hover:text-bundle-green-dark inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Suggest a tag
        </button>
      </div>

      {/* Selected tags strip — easy to unpick */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded-xl bg-bundle-green/5 border border-bundle-green/15 p-2.5">
          {value.map((t) => {
            const clean = t.replace(/^#/, '');
            return (
              <button
                key={clean}
                type="button"
                onClick={() => toggle(clean)}
                className="inline-flex items-center gap-1 rounded-full bg-bundle-green text-white px-2.5 py-1 text-xs font-bold"
              >
                #{clean}
                <X className="w-3 h-3" />
              </button>
            );
          })}
        </div>
      )}

      {/* Search filter */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tags…"
          data-testid="bundle-tags-search"
          className="w-full rounded-full border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green focus:border-transparent"
        />
      </div>

      {/* Categorized catalog */}
      {!catalog && <p className="text-xs text-gray-500">Loading tags…</p>}
      {catalog && Object.keys(filteredCatalog).length === 0 && (
        <p className="text-xs text-gray-500">No tags match &quot;{query}&quot;. Try suggesting a new one.</p>
      )}
      {Object.entries(filteredCatalog).map(([category, tags]) => (
        <div key={category} className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{category}</p>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => {
              const isSelected = selected.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  data-testid={`bundle-tag-${t}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${
                    isSelected
                      ? 'bg-bundle-green text-white'
                      : 'bg-white text-bundle-green border border-bundle-green/30 hover:border-bundle-green hover:bg-bundle-green/5'
                  }`}
                >
                  {isSelected ? <Check className="w-3 h-3" /> : null}
                  #{t}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {suggesting && (
        <SuggestTagModal
          onClose={() => setSuggesting(false)}
          categories={Object.keys(catalog || {})}
          onSubmitted={(name) => {
            toast.success(`#${name} submitted — admin will review.`);
            setSuggesting(false);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Suggest Modal
// ──────────────────────────────────────────────────────────────────────
function SuggestTagModal({ onClose, categories, onSubmitted }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState(categories[0] || 'Interests');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/tags/suggest`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, category, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to suggest tag');
        return;
      }
      onSubmitted(data.name);
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
        <h3 className="font-heading font-bold text-lg text-bundle-green mb-3">Suggest a new tag</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-700">Tag name (no spaces, letters/numbers only)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9]/g, ''))}
              placeholder="e.g. PickleballToo"
              maxLength={30}
              data-testid="suggest-tag-name"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="suggest-tag-category"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">Why? (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="A short note helps admins approve."
              data-testid="suggest-tag-reason"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 rounded-full border border-gray-300 py-2 text-sm font-bold text-gray-700">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || name.length < 2}
            data-testid="suggest-tag-submit"
            className="flex-1 rounded-full bg-bundle-green text-white py-2 text-sm font-bold disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
